'use client';

import { useState, useTransition } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Alert, AlertDescription } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import { Input } from '@kit/ui/input';
import { Trans } from '@kit/ui/trans';

import { useCaptcha } from '../captcha/client';
import { requestPasswordResetAction } from '../server/password-reset.actions';
import { AuthErrorAlert } from './auth-error-alert';

const PasswordResetSchema = z.object({
  email: z.string().email(),
});

export function PasswordResetRequestContainer(params: {
  redirectPath: string;
  defaultEmail?: string;
}) {
  const { t } = useTranslation('auth');
  const [isPending, startTransition] = useTransition();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const captcha = useCaptcha();

  const form = useForm({
    resolver: zodResolver(PasswordResetSchema),
    defaultValues: {
      email: params.defaultEmail ?? '',
    },
  });

  const handleSubmit = (data: z.infer<typeof PasswordResetSchema>) => {
    const redirectTo = new URL(params.redirectPath, window.location.origin)
      .href;

    startTransition(async () => {
      try {
        const result = await requestPasswordResetAction({
          email: data.email,
          redirectTo,
        });

        // A transient provider send-failure returns a structured result
        // (rather than throwing) so we can show a friendly retry message
        // instead of a generic error.
        if (!result.success) {
          setError(new Error(result.error));
          setSuccess(false);
          return;
        }

        setSuccess(true);
        setError(null);
      } catch (err) {
        console.error('[Password Reset] Server action threw error', {
          error: err,
          timestamp: new Date().toISOString(),
        });
        setError(
          err instanceof Error ? err : new Error('Failed to reset password'),
        );
      }
    });
  };

  return (
    <>
      <If condition={success}>
        <Alert variant={'success'}>
          <AlertDescription>
            <Trans i18nKey={'auth:passwordResetSuccessMessage'} />
          </AlertDescription>
        </Alert>
      </If>

      <If condition={!success}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className={'w-full'}>
            <div className={'flex flex-col gap-y-4'}>
              <AuthErrorAlert error={error} />

              <FormField
                name={'email'}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Trans i18nKey={'common:emailAddress'} />
                    </FormLabel>

                    <FormControl>
                      <Input
                        required
                        type="email"
                        placeholder={t('emailPlaceholder')}
                        {...field}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button disabled={isPending} type="submit">
                <Trans i18nKey={'auth:passwordResetLabel'} />
              </Button>
            </div>
            {captcha.field}
          </form>
        </Form>
      </If>
    </>
  );
}
